import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';

/**
 * Export data to CSV format and trigger download
 * @param data Array of objects to export
 * @param filename Filename for the downloaded file (without extension)
 */
export function exportToCSV(data: any[], filename: string): void {
  const blob = toCsvBlob(data);
  triggerDownload(blob, `${filename}.csv`);
}

/** The CSV-building half of {@link exportToCSV}, without triggering a download -- reused
 *  by zip-export.utils.ts to embed the same CSV content inside a zip archive. */
export function toCsvBlob(data: any[]): Blob {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Convert data to CSV using PapaParse
  const csv = Papa.unparse(data, {
    header: true,
    quotes: true, // Quote all fields to handle special characters
  });

  // Add UTF-8 BOM for Excel compatibility
  const BOM = '\uFEFF';
  return new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
}

/**
 * Export data to XLSX (Excel) format and trigger download
 * @param data Array of objects to export
 * @param filename Filename for the downloaded file (without extension)
 * @param sheetName Name of the worksheet tab (defaults to 'Submissions')
 */
export function exportToXLSX(data: any[], filename: string, sheetName = 'Submissions'): void {
  const blob = toXlsxBlob(data, sheetName);
  triggerDownload(blob, `${filename}.xlsx`);
}

/** The XLSX-building half of {@link exportToXLSX}, without triggering a download -- reused
 *  by zip-export.utils.ts to embed the same workbook inside a zip archive. */
export function toXlsxBlob(data: any[], sheetName = 'Submissions'): Blob {
  if (!data || data.length === 0) {
    throw new Error('No data to export');
  }

  // Create worksheet from data
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Auto-size columns (approximate)
  const columnWidths: { wch: number }[] = [];
  const headers = Object.keys(data[0] || {});

  headers.forEach((header, index) => {
    let maxWidth = header.length;
    data.forEach((row) => {
      const cellValue = String(row[header] || '');
      maxWidth = Math.max(maxWidth, cellValue.length);
    });
    columnWidths[index] = { wch: Math.min(maxWidth + 2, 50) }; // Cap at 50 characters
  });

  worksheet['!cols'] = columnWidths;

  // Create workbook and add worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // Generate binary string
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

  return new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Trigger browser download for a blob
 * @param blob Blob to download
 * @param filename Filename for the download
 */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = window.document.createElement('a');
  link.href = url;
  link.download = filename;
  window.document.body.appendChild(link);
  link.click();

  // Clean up
  window.document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Generate a filename with timestamp
 * @param prefix Filename prefix (e.g., 'submissions')
 * @param name Optional name to include
 * @returns Formatted filename without extension
 */
export function generateFilename(prefix: string, name?: string): string {
  // Cosmetic filename suffix, not app data -- and this free function has no DI access to
  // ClockSyncService anyway.
  // eslint-disable-next-line no-restricted-syntax
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
  const sanitizedName = name ? name.replace(/[^a-zA-Z0-9-_]/g, '_').substring(0, 30) : '';

  if (sanitizedName) {
    return `${prefix}_${sanitizedName}_${timestamp}`;
  }

  return `${prefix}_${timestamp}`;
}
